import {Divider, LinearProgress, Link, Stack, Tooltip} from "@mui/material";
import {FC, useMemo} from "react";
import Box from "@mui/material/Box";
import {useTranslation} from "react-i18next";
import Typography from "@mui/material/Typography";
import {getLinearProgressColor, getTextColorOnRecommended} from "../../../util/utils";
import {IProfileWish, IWish, WishType} from "../../../store/wish/Helper";
import {RoutePaths} from "../../../util/constants";
import {useTheme} from "@mui/material/styles";
import {RootState, useAppSelector} from "../../../store";
import {computeSeededTicketsForWish} from "./TicketsCountLinearProgressHelper";

interface Props {
    wish: IWish | IProfileWish;
    recommended: boolean;
    accumulateTotals?: boolean;
}

const TicketsCountLinearProgress: FC<Props> = ({accumulateTotals = false, ...props}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);

    const wishes = useMemo(() => [props.wish, ...(props.wish.children ?? [])], [props.wish]);

    const total = useMemo(
        () =>
            accumulateTotals
                ? wishes.reduce((acc, w) => acc + w.max_tickets, 0)
                : props.wish.max_tickets,
        [accumulateTotals, wishes, props.wish.max_tickets]
    );

    const displayedCurrent = useMemo(() => {
        if (props.wish.name_en.toLowerCase().includes("coming soon")) {
            return props.wish.tickets_count;
        }

        const nowMs = serverTime ? new Date(serverTime).getTime() : Date.now();
        const list = accumulateTotals ? wishes : [props.wish];

        return list.reduce(
            (acc, w) =>
                acc +
                computeSeededTicketsForWish({
                    id: w.id,
                    actual: w.tickets_count,
                    max: w.max_tickets,
                    startISO: w.start_time,
                    endISO: w.end_time,
                    nowMs,
                }),
            0
        );
    }, [accumulateTotals, wishes, props.wish, serverTime]);

    const color = getLinearProgressColor(displayedCurrent, total);
    const getLabelColor = useMemo(
        () => getTextColorOnRecommended(props.recommended),
        [props.recommended]
    );

    const boughtLabel =
        props.wish.type === WishType.VOUCHER && props.wish.cost !== 0
            ? t("bought")
            : t("obtained");

    const handleLinearProgressClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleReadMoreClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <Box>
            {/* Label only */}
            <Typography variant="subtitle1" sx={{color: getLabelColor, mb: 0.5}}>
                {props.wish.type === WishType.VOUCHER ? t("vouchers") : t("entries")}:
            </Typography>

            <Tooltip
                enterTouchDelay={0}
                title={
                    <Box textAlign="center">
                        <Stack
                            spacing={2}
                            divider={
                                <Divider
                                    sx={{backgroundColor: theme.palette.primary.main}}
                                    orientation="vertical"
                                    flexItem
                                />
                            }
                            direction="row"
                        >
                            <Box textAlign="center">
                                <Typography variant="subtitle2" fontWeight={600}>
                                    {boughtLabel}
                                </Typography>
                                <Typography variant="subtitle2">{displayedCurrent}</Typography>
                            </Box>
                            <Box textAlign="center">
                                <Typography variant="subtitle2" fontWeight={600}>
                                    {t("available")}
                                </Typography>
                                <Typography variant="subtitle2">
                                    {Math.max(total - displayedCurrent, 0)}
                                </Typography>
                            </Box>
                        </Stack>
                        <Link
                            color="primary"
                            href={RoutePaths.GUIDE}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{mt: 0.5}}
                            onClick={handleReadMoreClick}
                        >
                            ({t("read_more")})
                        </Link>
                    </Box>
                }
                arrow
                placement="top"
            >
                <Box position="relative">
                    <LinearProgress
                        variant="determinate"
                        color={color}
                        value={total > 0 ? Math.round((displayedCurrent / total) * 100) : 0}
                        onMouseDown={handleLinearProgressClick}
                        onTouchStart={handleLinearProgressClick}
                        onClick={handleLinearProgressClick}
                        sx={{
                            height: 18,
                            borderRadius: "3px"
                        }}
                    />

                    {/* Bought / total centered inside the bar */}
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        color="primary"
                        sx={{
                            position: "absolute",
                            top: "50%",
                            textShadow: "0.5px 0.5px 2.5px rgba(0,0,0,0.8)",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                        }}
                    >
                        {displayedCurrent} / {total}
                    </Typography>
                </Box>
            </Tooltip>
        </Box>
    );
};

export default TicketsCountLinearProgress;
